/**
 * Componente para renderizar condicionalmente baseado em permissões
 */

import { ReactNode } from "react";
import { usePermissions } from "@/hooks/usePermissions";

interface PermissionGuardProps {
  children: ReactNode;
  permission?: string;
  permissions?: string[];
  requireAll?: boolean;
  fallback?: ReactNode;
}

/**
 * Renderiza children apenas se o usuário tiver as permissões necessárias
 */
export function PermissionGuard({
  children,
  permission,
  permissions,
  requireAll = false,
  fallback = null,
}: PermissionGuardProps) {
  const { hasPermission, hasAnyPermission, hasAllPermissions, isAdmin } =
    usePermissions();

  // Admin sempre tem acesso
  if (isAdmin) {
    return <>{children}</>;
  }

  // Verificar permissão única
  if (permission) {
    return hasPermission(permission) ? <>{children}</> : <>{fallback}</>;
  }

  // Verificar múltiplas permissões
  if (permissions && permissions.length > 0) {
    const hasAccess = requireAll
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions);

    return hasAccess ? <>{children}</> : <>{fallback}</>;
  }

  // Sem permissões especificadas, renderiza children
  return <>{children}</>;
}

/**
 * Renderiza children apenas se o usuário for admin
 */
export function AdminOnly({
  children,
  fallback = null,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { isAdmin } = usePermissions();
  return isAdmin ? <>{children}</> : <>{fallback}</>;
}

/**
 * Renderiza children apenas se o usuário NÃO tiver a permissão
 */
export function NoPermissionGuard({
  children,
  permission,
  fallback = null,
}: {
  children: ReactNode;
  permission: string;
  fallback?: ReactNode;
}) {
  const { hasPermission, isAdmin } = usePermissions();

  // Admin sempre é bloqueado neste caso (não renderiza children)
  if (isAdmin) {
    return <>{fallback}</>;
  }

  return !hasPermission(permission) ? <>{children}</> : <>{fallback}</>;
}
